import { useAppSelector } from "src/redux/hooks";
import { RootState } from "src/redux/store";
import { ClaimSubmittedStatus, LineOfBusiness } from "src/app/types";
import { ProcessedApiResponse } from "src/api/types";

export interface ClaimSubmissionState {
  response?: ProcessedApiResponse;
  status?: ClaimSubmittedStatus;
  lineOfBusiness: LineOfBusiness;
  workersCompensationTpa: string;
  amazonClaimReferenceId?: string;
}

/**
 * Resolves the correct submission response and status from Redux
 * based on the current line of business. Eliminates repeated
 * ternary chains across the component.
 */
export const useClaimSubmissionState = (): ClaimSubmissionState => {
  const submitScreenStatus = useAppSelector(
    (state: RootState) => state.app.submitScreenStatus
  );
  const createWcClaimResponse = useAppSelector(
    (state: RootState) => state.app.createWcClaimResponse
  );
  const lineOfBusiness = useAppSelector(
    (state: RootState) => state.app.lineOfBusiness
  );
  const cargoSubmitScreenStatus = useAppSelector(
    (state: RootState) => state.cargoApp.submitScreenStatus
  );
  const createCargoClaimResponse = useAppSelector(
    (state: RootState) => state.cargoApp.createCargoClaimResponse
  );
  const iplSubmitScreenStatus = useAppSelector(
    (state: RootState) => state.iplApp.submitScreenStatus
  );
  const createIPLClaimResponse = useAppSelector(
    (state: RootState) => state.iplApp.createIPLClaimResponse
  );
  const amazonClaimReferenceId = useAppSelector(
    (state: RootState) => state.iplApp.amazonClaimReferenceId
  );
  const workersCompensationTpa = useAppSelector(
    (state: RootState) => state.forms.workersCompensationTpa
  );

  const responseMap: Record<LineOfBusiness, ProcessedApiResponse | undefined> =
    {
      [LineOfBusiness.WORKERS_COMPENSATION]: createWcClaimResponse,
      [LineOfBusiness.CARGO]: createCargoClaimResponse,
      [LineOfBusiness.IPL]: createIPLClaimResponse,
    };

  const statusMap: Record<LineOfBusiness, ClaimSubmittedStatus | undefined> = {
    [LineOfBusiness.WORKERS_COMPENSATION]: submitScreenStatus,
    [LineOfBusiness.CARGO]: cargoSubmitScreenStatus,
    [LineOfBusiness.IPL]: iplSubmitScreenStatus,
  };

  return {
    response: responseMap[lineOfBusiness],
    status: statusMap[lineOfBusiness],
    lineOfBusiness,
    workersCompensationTpa,
    amazonClaimReferenceId,
  };
};
